"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MatchListBlock, type MatchListItem, BettingOdds } from "fansunited-sports-ui";
import { useServerBatchMatchOdds } from "@/features/odds/useServerBatchMatchOdds";
import { toMatchOddsMap } from "@/features/odds/adapters";
import type { OddsData } from "@/features/odds/types";
import { useBetslip } from "@/features/betslip/useBetslip";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useOddsDisplay } from "@/features/odds/useOddsDisplay";

interface MatchListProps {
    matches: MatchListItem[];
    variant: "teamFixtures" | "teamResults";
    emptyMessage: string;
    /** Hidden lists pass false so they never request odds. Defaults to true. */
    active?: boolean;
}

/**
 * MatchList component displays a list of matches with optional betting odds
 * Fetches odds for all matches in a single batch API call for optimal performance
 * Only fetches odds for upcoming matches within 2 weeks (odds not available for past or far future matches)
 */
export const MatchList: React.FC<MatchListProps> = ({ matches, variant, emptyMessage, active = true }) => {
    // Detect mobile viewport for flash mode (only cycle odds on small screens)
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 767px)");
        setIsMobile(mql.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);

    // Extract match IDs for batch fetching - only for upcoming matches within 2 weeks
    const matchIds =
        active && variant === "teamFixtures"
            ? matches
                  .filter((m) => {
                      const kickoffTime = m.match.metadata?.kickoffTime;
                      if (!kickoffTime) return false;

                      const now = new Date();
                      const matchDate = new Date(kickoffTime);
                      const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

                      // Only include matches that are in the future and within 2 weeks
                      return matchDate > now && matchDate <= twoWeeksFromNow;
                  })
                  .map((m) => m.match.id)
            : [];

    // Fetch odds for all matches in a single API call
    const { oddsMap: rawOddsMap, loading } = useServerBatchMatchOdds({
        matchIds,
        markets: ["1x2"],
    });

    const oddsMap = useMemo(() => toMatchOddsMap(rawOddsMap), [rawOddsMap]);

    // Betslip integration
    const { addOddsSelection, canAddOdds } = useBetslip();
    const tenantSettings = useTenantSettings();
    const tenantOdds = useOddsDisplay();

    const handleOddsClick = useCallback(
        (item: MatchListItem, odd: OddsData) => {
            if (!canAddOdds) return;
            addOddsSelection(item.match.id, "FT_1X2", odd.label, item.match.metadata?.kickoffTime);
        },
        [canAddOdds, addOddsSelection]
    );

    if (matches.length === 0) {
        return <div className="text-center py-12 text-muted-foreground">{emptyMessage}</div>;
    }

    // Helper to check if a match is eligible for odds (upcoming and within 2 weeks)
    const isEligibleForOdds = (item: MatchListItem): boolean => {
        const kickoffTime = item.match.metadata?.kickoffTime;
        if (!kickoffTime) return false;

        const now = new Date();
        const matchDate = new Date(kickoffTime);
        const twoWeeksFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

        return matchDate > now && matchDate <= twoWeeksFromNow;
    };

    // Add betting odds to fixtures using the batch-fetched data
    const matchesWithOdds = matches.map((item) => {
        if (variant === "teamFixtures" && isEligibleForOdds(item) && tenantSettings.hasOdds) {
            const matchOdds = oddsMap.get(item.match.id);

            // Show loading skeleton while fetching (only for eligible matches)
            if (loading) {
                return {
                    ...item,
                    bettingOdds: (
                        <div className="flex gap-2 animate-pulse">
                            <div className="flex-1 h-8 bg-muted rounded"></div>
                            <div className="flex-1 h-8 bg-muted rounded"></div>
                            <div className="flex-1 h-8 bg-muted rounded"></div>
                        </div>
                    ),
                };
            }

            // Show odds if available
            if (matchOdds && matchOdds.length > 0) {
                // Process odds to add betslip click handlers when in predictions-and-odds mode
                const processedOdds = canAddOdds
                    ? matchOdds.map((bookmakerOdds) => ({
                          ...bookmakerOdds,
                          odds: bookmakerOdds.odds.map((odd) => ({
                              ...odd,
                              onClick: () => handleOddsClick(item, odd),
                              // Strip URL to render as button instead of link
                              url: undefined,
                          })),
                      }))
                    : matchOdds;

                return {
                    ...item,
                    bettingOdds: (
                        <div className="space-y-3">
                            {processedOdds.map((bookmakerOdds, index) => (
                                <BettingOdds
                                    key={`${bookmakerOdds.operator.name}-${index}`}
                                    odds={bookmakerOdds.odds}
                                    operator={bookmakerOdds.operator}
                                    styles={{
                                        container: "p-0",
                                        operatorContainer: "p-0",
                                        oddsContainer: "px-2 py-1.5 odds-button",
                                        // Mobile: keep deliberate 3000ms flash for narrow rows.
                                        // Desktop: defer to tenant setting (default = no flash).
                                        ...(isMobile
                                            ? { mode: "flash" as const, flashInterval: 3000 }
                                            : { mode: tenantOdds.displayMode, flashInterval: tenantOdds.flashInterval }),
                                    }}
                                />
                            ))}
                        </div>
                    ),
                };
            }
        }
        return item;
    });

    return (
        <div className="bg-card text-card-foreground p-3">
            <MatchListBlock
                variant={variant}
                items={matchesWithOdds}
                styles={{
                    matchScoreStyles: {
                        typography: {
                            teamName: "text-sm lg:text-md font-semibold",
                        },
                        logoSize: "lg",
                    },
                    divider: {
                        color: "divide-foreground/10",
                    },
                }}
            />
        </div>
    );
};
